/**
 * The application navigation, as data.
 *
 * Before this the nav was nine `<Link>`s hand-written into
 * `routes/__root.tsx`, each repeating the same `className`, with no way to
 * ask "which section am I in" and no way to hide one. Two things fall out
 * of making it a list instead:
 *
 * 1. The sidebar, the header breadcrumb and the tests all read the same
 *    source. A link that exists in the chrome but not in the breadcrumb is
 *    no longer possible to write.
 * 2. Entitlements get somewhere to land. `product` marks the two sections
 *    that are products rather than platform plumbing — Sessions and Vault.
 *    `navForProducts` is the filter that field was added for: the tenant's
 *    `products` arrive on the root of `GET /hierarchy`, and the shell hands
 *    them here.
 *
 * This module is deliberately free of React. It is pure data plus the
 * pathname maths the shell needs, so `nav.test.ts` can cover the active-link
 * rules without rendering a router.
 */

import type { TenantProduct } from '@stoaria-control/schema'

/** The two sections that are products in their own right. Platform pages
 * have no product: they are the machinery behind both.
 *
 * Derived from the API's `TenantProduct` rather than restated, so a third
 * product cannot be sold without this list failing to compile. */
export type NavProduct = TenantProduct

/**
 * Every destination the chrome can link to.
 *
 * Written out as a literal union rather than `string` so that TanStack
 * Router's typed `<Link to>` still checks these against the generated route
 * tree — a deleted or renamed route fails the build here instead of 404ing
 * at 3am.
 */
export type NavTo =
  | '/sessions'
  | '/vault/projects'
  | '/vault/messages'
  | '/vault/files'
  | '/vault/principals'
  | '/vault/approvals'
  | '/vault/daemons'
  | '/vault/audit'
  | '/vault/tenant-key'
  | '/projects'
  | '/registry/$kind'
  | '/memory'
  | '/alerts'
  | '/composio'
  | '/dashboard'

export interface NavItem {
  /** What the sidebar and the breadcrumb call it. */
  label: string
  to: NavTo
  /** Path params for the one route that takes them (`/registry/$kind`).
   * The registry is a single route over seven entity kinds; the sidebar
   * points at harnesses because that is the one an operator opens first. */
  params?: { kind: string }
  /**
   * The pathname prefix that means "this item is the current page", for the
   * cases where it is not simply `to`.
   *
   * `/registry/$kind` is the whole reason this exists: `to` carries the
   * param placeholder, so the literal string never equals the pathname the
   * browser is actually on (`/registry/cli-profiles`). Matching on
   * `/registry` keeps the item lit for every kind.
   */
  activePath?: string
}

export interface NavSection {
  /** Stable key for React and for tests; not shown to anyone. */
  id: string
  label: string
  /** The product this section belongs to, or absent for platform pages
   * that come with every tenant. Read by `navForProducts`. */
  product?: NavProduct
  items: readonly NavItem[]
}

/**
 * The sidebar, top to bottom.
 *
 * Order within a section is by how often an operator opens the page, not
 * alphabetically: Vault starts at Projects & Items because that is where
 * secrets are read and written, and Approvals sits third rather than first
 * because the queue is usually empty.
 */
export const NAV: readonly NavSection[] = [
  {
    id: 'sessions',
    label: 'Sessions',
    product: 'sessions',
    items: [{ label: 'Sessions', to: '/sessions' }],
  },
  {
    id: 'vault',
    label: 'Vault',
    product: 'vault',
    items: [
      { label: 'Projects', to: '/vault/projects' },
      // Messages and Files sit second and third, directly under the
      // place secrets are read and written and above the administrative
      // pages. They are correspondence: an operator opens them because
      // something arrived, which is a more frequent reason to come here
      // than editing a principal.
      { label: 'Messages', to: '/vault/messages' },
      { label: 'Files', to: '/vault/files' },
      { label: 'Principals', to: '/vault/principals' },
      { label: 'Approvals', to: '/vault/approvals' },
      { label: 'Daemons', to: '/vault/daemons' },
      { label: 'Audit', to: '/vault/audit' },
      { label: 'Tenant key', to: '/vault/tenant-key' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    product: undefined,
    items: [
      { label: 'Projects', to: '/projects' },
      {
        label: 'Registry',
        to: '/registry/$kind',
        params: { kind: 'harnesses' },
        activePath: '/registry',
      },
      { label: 'Memory', to: '/memory' },
      { label: 'Alerts', to: '/alerts' },
      { label: 'Composio', to: '/composio' },
      { label: 'Dashboard', to: '/dashboard' },
    ],
  },
]

/**
 * `NAV` as this tenant may see it: product sections it has not bought are
 * dropped, platform sections always stay.
 *
 * COSMETIC, and deliberately so. The API refuses an unentitled tenant's
 * `/vault/**` and `/sessions**` calls with the same 403 a non-member gets,
 * so this is the sidebar agreeing with the server rather than the control
 * itself — typing the URL in by hand gets the operator a refused page, not
 * a hidden one. Never move an entitlement decision here.
 *
 * `products` is the root's `products` from `GET /hierarchy`, by way of
 * `lib/tenant-products.ts`. An empty list leaves only Platform, which is
 * the honest rendering of a tenant that has bought nothing.
 *
 * `null` means the question could not be answered — the fetch failed, or
 * nobody is signed in (the shell renders on `/login` too). That is NOT
 * the same as "bought nothing", and it returns the whole nav. Failing
 * open is correct for a cosmetic filter: a section that should not be
 * shown costs one 403 when it is clicked, while a section wrongly hidden
 * by a blip reads as an account that has lost a product.
 */
export function navForProducts(
  products: readonly NavProduct[] | null,
): readonly NavSection[] {
  if (products === null) return NAV
  return NAV.filter(
    (section) =>
      section.product === undefined || products.includes(section.product),
  )
}

/** The prefix an item is lit for. `activePath` when it is given, otherwise
 * the destination itself. */
export function navItemActivePath(item: NavItem): string {
  return item.activePath ?? item.to
}

/**
 * Is `pathname` inside this item?
 *
 * Prefix matching, not equality, so a detail page under a section keeps its
 * parent lit — and the boundary is a `/` rather than a bare `startsWith`,
 * which would light `/projects` while the operator is on `/projects-move`.
 */
export function isNavItemActive(pathname: string, item: NavItem): boolean {
  const base = navItemActivePath(item)
  return pathname === base || pathname.startsWith(`${base}/`)
}

export interface ActiveNavMatch {
  section: NavSection
  item: NavItem
}

/**
 * The deepest nav item `pathname` sits under, or null off the nav entirely
 * (`/`, `/login`, `/terminal/$sessionId`).
 *
 * "Deepest" matters for Vault: `/vault/tenant-key` matches only the tenant
 * key item today, but if a shorter prefix is ever added to the list the
 * longest match is the one an operator would name. Ties cannot happen —
 * two items with the same prefix would be the same page.
 */
export function activeNavItem(pathname: string): ActiveNavMatch | null {
  let best: ActiveNavMatch | null = null
  for (const section of NAV)
    for (const item of section.items) {
      if (!isNavItemActive(pathname, item)) continue
      const bestLength =
        best === null ? -1 : navItemActivePath(best.item).length
      if (navItemActivePath(item).length > bestLength) best = { section, item }
    }
  return best
}

/**
 * The header trail for a pathname: section then page, e.g.
 * `['Vault', 'Approvals']`.
 *
 * A one-item section (Sessions) collapses to just the page, because
 * "Sessions / Sessions" is noise. Off-nav pathnames get an empty trail and
 * the header renders nothing rather than inventing a crumb.
 */
export function breadcrumbFor(pathname: string): readonly string[] {
  const match = activeNavItem(pathname)
  if (match === null) return []
  if (match.section.items.length === 1) return [match.item.label]
  return [match.section.label, match.item.label]
}

/**
 * Is this a vault page?
 *
 * The header's Lock button hangs off this: the tenant private key is held
 * in page memory by `lib/vault-unlock.ts`, and the control that drops it
 * belongs on the screens where it is in use, not on every page in the app.
 */
export function isVaultPath(pathname: string): boolean {
  return pathname === '/vault' || pathname.startsWith('/vault/')
}
